import { Option } from '@sapphire/result';

import { CACHE_TTL, isCacheStale, Nullable } from '@ps/utils';
import { SurveyQuestion } from '@ps/survey-utils';

/** A cached value paired with the timestamp it was cached at, keyed together so the two can never drift out of sync. */
export interface CachedEntry<T> {
  data: T;
  cachedAt: Nullable<number>;
}

/**
 * Cache key for a question-library search. Sorting the ids and normalising the
 * phrase makes `age` + `[7, 3]` and `Age ` + `[3, 7]` share one cache entry.
 * @param search - free-text search phrase
 * @param categoryIds - selected category filter ids
 * @returns the record key for this search
 */
export const surveyQuestionsCacheKey = (
  search: string,
  categoryIds: number[],
): string =>
  `${search.trim().toLowerCase()}|${[...categoryIds]
    .sort((first, second) => first - second)
    .join(',')}`;

/**
 * Test if a question search is cached and not stale. Unlike
 * {@link isChildSurveysCacheValid} an empty array is a valid cached answer —
 * "no questions match this phrase" must not re-fetch on every keystroke.
 * @param key - key produced by {@link surveyQuestionsCacheKey}
 * @param questionsRecord - cached questions (with their cachedAt) by search key
 * @returns true if the search is cached and not stale.
 */
export const isSurveyQuestionsCacheValid = (
  key: string,
  questionsRecord: Record<string, CachedEntry<SurveyQuestion[]>>,
): boolean =>
  key in questionsRecord &&
  !isCacheStale(questionsRecord[key].cachedAt, CACHE_TTL.DEFAULT);

export const mostRecentCacheKey = (
  cachedAt: Record<string, Nullable<number>>,
): Nullable<string> => {
  const cachedEntries = Object.entries(cachedAt).filter(
    (entry): entry is [string, number] => !!entry[1],
  );

  return Option.from(cachedEntries.length ? cachedEntries : null).mapOr(
    null,
    (entries) =>
      entries.reduce((latest, entry) =>
        entry[1] > latest[1] ? entry : latest,
      )[0],
  );
};
